/**
 * Admin dashboard chart palette — blue-led.
 *
 * Blue carries volume, inflow, and everything neutral. The three semantic
 * exceptions stay warm on purpose: a business reader scans a cash-flow chart
 * for "which bar is money leaving", and an all-blue chart destroys that signal.
 *   money out → orange · risk → rose · credits → amber
 *
 * Admin-local by design. The shared @kioskgaming/ui palette is used by the
 * agent / super-agent / shop portals, which are orange and emerald — recoloring
 * it here would repaint three other products.
 */

/** Ordered blue ramp for multi-series charts. Darkest first: the primary series leads. */
export const BLUE_RAMP = [
  '#1e3a8a',
  '#1d4ed8',
  '#2563eb',
  '#3b82f6',
  '#60a5fa',
  '#93c5fd',
] as const;

export const CHART_INK = {
  muted: '#64748b',
  grid: '#e2e8f0',
  baseline: '#cbd5e1',
  primary: '#0f172a',
} as const;

export const CHART_COLORS = {
  /** Money in — blue leads. */
  in: '#2563eb',
  /** Money out — deliberately warm so inflow vs outflow reads at a glance. */
  out: '#f97316',
  /** Net line — navy, distinguished from the bars by mark type as well as shade. */
  net: '#1e3a8a',
  fees: '#0ea5e9',
  credits: '#d97706',
  risk: '#e11d48',
  /** Hierarchy tiers ramp light-to-dark with seniority. */
  superAgent: '#1e3a8a',
  agent: '#3b82f6',
  shop: '#93c5fd',
  neutral: '#94a3b8',
};

export const PIE_COLORS = ['#1d4ed8', '#3b82f6', '#60a5fa', '#93c5fd', '#0ea5e9', '#f97316'];

export function formatUsdCompact(value: number): string {
  const safe = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: Math.abs(safe) >= 10000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(safe) >= 10000 ? 1 : 0,
  }).format(safe);
}

export function formatUsdFull(value: number): string {
  const safe = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(safe);
}

export function formatDayLabel(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * Axis label for a backend bucket key. Hour buckets arrive as
 * `YYYY-MM-DDTHH:00` and render as `09:00`; day buckets as `YYYY-MM-DD` and
 * render as `Jul 14`. Anything unrecognised is passed through rather than
 * showing "Invalid Date".
 */
export function formatBucketLabel(bucket: string): string {
  if (typeof bucket !== 'string' || bucket === '') return '';
  const hourMatch = bucket.match(/^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2})$/);
  if (hourMatch) return `${hourMatch[1]}:${hourMatch[2]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(bucket)) return formatDayLabel(bucket);
  return bucket;
}
